var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/allocator.ts
var Allocator = class {
  constructor(n) {
    this.currentIndex = BigInt(1);
    this.active = {};
    this.freed = [];
    this.memory = new Uint8Array(n);
  }
  reset() {
    this.currentIndex = BigInt(1);
    this.active = {};
    this.freed = [];
  }
  alloc(length) {
    for (var i = 0; i < this.freed.length; i++) {
      let block = this.freed[i];
      if (block.length === length) {
        this.active[Number(block.offset)] = block;
        this.freed.splice(i, 1);
        return block.offset;
      } else if (block.length > length + BigInt(64)) {
        const newBlock = { offset: block.offset, length };
        block.offset += length;
        block.length -= length;
        return newBlock.offset;
      }
    }
    if (BigInt(this.memory.length) < this.currentIndex + length) {
      const tmp = new Uint8Array(Number(this.currentIndex + length + BigInt(64)));
      tmp.set(this.memory);
      this.memory = tmp;
    }
    const offset = this.currentIndex;
    this.currentIndex += length;
    this.active[Number(offset)] = { offset, length };
    return offset;
  }
  getBytes(offset) {
    const block = this.active[Number(offset)];
    if (!block) {
      return null;
    }
    return new Uint8Array(this.memory.buffer, Number(offset), Number(block.length));
  }
  getString(offset) {
    const bytes = this.getBytes(offset);
    if (bytes === null) {
      return null;
    }
    return new TextDecoder().decode(bytes);
  }
  allocBytes(data) {
    const offs = this.alloc(BigInt(data.length));
    const bytes = this.getBytes(offs);
    if (bytes === null) {
      this.free(offs);
      return BigInt(0);
    }
    bytes.set(data);
    return offs;
  }
  allocString(data) {
    const bytes = new TextEncoder().encode(data);
    return this.allocBytes(bytes);
  }
  getLength(offset) {
    const block = this.active[Number(offset)];
    if (!block) {
      return BigInt(0);
    }
    return block.length;
  }
  free(offset) {
    const block = this.active[Number(offset)];
    if (!block) {
      return;
    }
    delete this.active[Number(offset)];
    this.freed.push(block);
  }
};

// src/plugin.ts
var ExtismPlugin = class {
  constructor(moduleData, config) {
    this.moduleData = moduleData;
    this.allocator = new Allocator(1024 * 1024);
    this.config = config;
    this.vars = {};
    this.input = new Uint8Array();
    this.output = new Uint8Array();
  }
  getExports() {
    return __async(this, null, function* () {
      const module = yield this._instantiateModule();
      return module.instance.exports;
    });
  }
  getImports() {
    return __async(this, null, function* () {
      const module = yield this._instantiateModule();
      return WebAssembly.Module.imports(module.module);
    });
  }
  getInstance() {
    return __async(this, null, function* () {
      const module = yield this._instantiateModule();
      return module.instance;
    });
  }
  call(func_name, input) {
    return __async(this, null, function* () {
      const module = yield this._instantiateModule();
      if (typeof input === "string") {
        this.input = new TextEncoder().encode(input);
      } else if (input instanceof Uint8Array) {
        this.input = input;
      } else {
        throw new Error("input should be string or Uint8Array");
      }
      this.allocator.reset();
      let func = module.instance.exports[func_name];
      if (!func) {
        throw Error(`function does not exist ${func_name}`);
      }
      func();
      return this.output;
    });
  }
  _instantiateModule() {
    return __async(this, null, function* () {
      if (this.module) {
        return this.module;
      }
      const environment = this.makeEnv();
      this.module = yield WebAssembly.instantiate(this.moduleData, { env: environment });
      return this.module;
    });
  }
  makeEnv() {
    const plugin = this;
    return {
      extism_alloc(n) {
        return plugin.allocator.alloc(n);
      },
      extism_free(n) {
        plugin.allocator.free(n);
      },
      extism_load_u8(n) {
        return plugin.allocator.memory[Number(n)];
      },
      extism_load_u64(n) {
        let cast = new DataView(plugin.allocator.memory.buffer, Number(n));
        return cast.getBigUint64(0, true);
      },
      extism_store_u8(offset, n) {
        plugin.allocator.memory[Number(offset)] = Number(n);
      },
      extism_store_u64(offset, n) {
        const tmp = new DataView(plugin.allocator.memory.buffer, Number(offset));
        tmp.setBigUint64(0, n, true);
      },
      extism_input_length() {
        return BigInt(plugin.input.length);
      },
      extism_input_load_u8(i) {
        return plugin.input[Number(i)];
      },
      extism_input_load_u64(idx) {
        let cast = new DataView(plugin.input.buffer, Number(idx));
        return cast.getBigUint64(0, true);
      },
      extism_output_set(offset, length) {
        const offs = Number(offset);
        const len = Number(length);
        plugin.output = plugin.allocator.memory.slice(offs, offs + len);
      },
      extism_error_set(i) {
        throw plugin.allocator.getString(i);
      },
      extism_config_get(i) {
        if (typeof plugin.config === "undefined") {
          return BigInt(0);
        }
        const key = plugin.allocator.getString(i);
        if (key === null) {
          return BigInt(0);
        }
        const value = plugin.config.get(key);
        if (typeof value === "undefined") {
          return BigInt(0);
        }
        return plugin.allocator.allocString(value);
      },
      extism_var_get(i) {
        const key = plugin.allocator.getString(i);
        if (key === null) {
          return BigInt(0);
        }
        const value = plugin.vars[key];
        if (typeof value === "undefined") {
          return BigInt(0);
        }
        return plugin.allocator.allocBytes(value);
      },
      extism_var_set(n, i) {
        const key = plugin.allocator.getString(n);
        if (key === null) {
          return;
        }
        const value = plugin.allocator.getBytes(i);
        if (value === null) {
          return;
        }
        plugin.vars[key] = value;
      },
      extism_http_request(n, i) {
        debugger;
        return 0;
      },
      extism_length(i) {
        return plugin.allocator.getLength(i);
      },
      extism_log_warn(i) {
        const s = plugin.allocator.getString(i);
        console.warn(s);
      },
      extism_log_info(i) {
        const s = plugin.allocator.getString(i);
        console.log(s);
      },
      extism_log_debug(i) {
        const s = plugin.allocator.getString(i);
        console.debug(s);
      },
      extism_log_error(i) {
        const s = plugin.allocator.getString(i);
        console.error(s);
      }
    };
  }
};

// src/context.ts
var ExtismContext = class {
  newPlugin(manifest, config) {
    return __async(this, null, function* () {
      let moduleData = null;
      if (manifest instanceof ArrayBuffer) {
        moduleData = manifest;
      } else if (manifest.wasm) {
        const wasmData = manifest.wasm;
        if (wasmData.length > 1)
          throw Error("This runtime only supports one module in Manifest.wasm");
        const wasm = wasmData[0];
        if (wasm.data) {
          moduleData = wasm.data;
        } else if (wasm.path) {
          const response = yield fetch(wasm.path);
          moduleData = yield response.arrayBuffer();
          console.dir(moduleData);
        }
      }
      if (!moduleData) {
        throw Error(`Unsure how to interpret manifest ${manifest}`);
      }
      return new ExtismPlugin(moduleData, config);
    });
  }
};
export {
  ExtismContext
};
